/**
 * Store cleanup utilities for logout and workspace switching.
 *
 * Centralizes the logic for clearing workspace-scoped Zustand stores
 * to ensure consistent behavior and avoid data leakage between workspaces.
 */

import { useTableStore } from '@/store/useTableStore';
import { useVersionStore } from '@/store/useVersionStore';
import { useFormStore } from '@/store/useFormStore';
import { useUndoStore } from '@/store/useUndoStore';
import { useUIStore } from '@/store/useUI';

/**
 * Clear all workspace-scoped store data.
 *
 * Call this when:
 * - User logs out (clears everything)
 * - User switches workspace (clears workspace-specific data)
 *
 * This does NOT clear:
 * - User preferences (theme, view mode, column widths) - persisted in localStorage
 * - TanStack Query cache - handle separately with queryClient.invalidateQueries()
 */
export function clearWorkspaceScopedStores(): void {
  // Clear table UI state (sorting, filters, pagination, row selection)
  useTableStore.getState().clearAllTableConfigs();

  // Clear version processing state
  useVersionStore.getState().reset();

  // Clear form state
  useFormStore.getState().resetForm('versionForm');

  // Clear undo action (prevents stale undo from previous workspace)
  useUndoStore.getState().clearAction();

  // Clear transient UI state (keeps theme and other persisted preferences)
  useUIStore.getState().resetUI();
}
